/**
 * PageNote Component - Displays custom notes and market insights at the top of pages
 * 
 * Features:
 * - Shows admin-created notes (per-tenant, per-page)
 * - Shows AI-generated market insights with trend context
 * - Supports markdown formatting
 * - Collapsible for better UX
 */

import { useState, useEffect } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronDown, 
  ChevronUp, 
  Info, 
  TrendingUp, 
  Globe,
  AlertCircle
} from "lucide-react";

export function PageNote({ pageId }) {
  const { sessionToken } = useAuth();
  const [pageNote, setPageNote] = useState(null);
  const [marketInsight, setMarketInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNotes = async () => {
      if (!sessionToken) return;
      
      const tenantId = sessionStorage.getItem("viewing_tenant_id");
      if (!tenantId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch both page note and market insight in parallel
        const [noteResponse, insightResponse] = await Promise.all([
          axios.get(`${API}/ai-analytics/tenants/${tenantId}/page-notes/${pageId}`, {
            params: { Authorization: `Bearer ${sessionToken}` }
          }).catch(err => {
            console.log("Page note not found:", err.response?.status);
            return { data: { note: null } };
          }),
          axios.get(`${API}/ai-analytics/tenants/${tenantId}/market-insights/${pageId}`, {
            params: { Authorization: `Bearer ${sessionToken}` }
          }).catch(err => {
            console.log("Market insight not found:", err.response?.status);
            return { data: { insight: null } };
          })
        ]);

        setPageNote(noteResponse.data?.note || null);
        setMarketInsight(insightResponse.data?.insight || null);
      } catch (err) {
        console.error("Error fetching page notes:", err);
        // Don't show error to user, just don't display the component
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [sessionToken, pageId]);

  // Don't render anything if loading, error, or no content
  if (loading) return null;
  if (!pageNote && !marketInsight) return null;

  return (
    <div className="mb-6 space-y-3" data-testid={`page-note-${pageId}`}>
      {/* Market Insight Card */}
      {marketInsight && marketInsight.status === "approved" && (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-blue-900">Market Context</span>
                    {marketInsight.market_context && (
                      <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                        <Globe className="w-3 h-3 mr-1" />
                        {marketInsight.market_context}
                      </Badge>
                    )}
                  </div>
                  <div className="prose prose-sm prose-slate max-w-none text-slate-700">
                    <ReactMarkdown>{marketInsight.insight_text}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Note Card */}
      {pageNote && (
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50">
          <CardContent className="p-4">
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                  <Info className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-amber-900">
                      {pageNote.title || "Admin Note"}
                    </span>
                  </div>
                  {isExpanded && (
                    <div className="prose prose-sm prose-slate max-w-none text-slate-700 mt-2">
                      <ReactMarkdown>{pageNote.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-amber-600 hover:text-amber-800 hover:bg-amber-100 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default PageNote;
